

import React, { useContext, useEffect, useMemo, useState } from "react";

import { GlobalContext } from "../../../Context/GlobalContext";

import useCatalogue from "../../../services/Order/UseCatalogue";

import NoIndex from "../../../components/NoIndex";

import { Image, RefreshCcw } from "lucide-react";

import LoadingScreen from "../../../utils/LoadingScreen";

const SellOnInstamart = () => {
  const { fetchCatalogue, syncInstamartItems } = useCatalogue();

  const { userID } = useContext(GlobalContext);

  const [loading, setLoading] = useState(false);

  const [catalogueStore, setCatalogueStore] = useState([]);

  const [search, setSearch] = useState("");

  const [selectedItems, setSelectedItems] = useState({});

  const [syncLoading, setSyncLoading] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const allcatalogueAPI = async (preserveScroll = false) => {
    const currentScrollY = preserveScroll ? window.scrollY || 0 : null;

    setLoading(true);

    try {
      const data = await fetchCatalogue();

      // fetchCatalogue can return either the categories array directly or

      // an API wrapper such as { responseCode, responseMessage, response }.

      // Normalize all supported response shapes before rendering.

      const extractCategories = (payload) => {
        if (Array.isArray(payload)) return payload;
        if (!payload || typeof payload !== "object") return [];

        // Current catalogue API format:
        // { response: { apiResponseDecoded: [...], comboList: [...] } }
        if (Array.isArray(payload?.response?.apiResponseDecoded)) {
          return payload.response.apiResponseDecoded;
        }

        // In case useCatalogue already returns response or decoded response.
        if (Array.isArray(payload?.apiResponseDecoded)) {
          return payload.apiResponseDecoded;
        }

        if (Array.isArray(payload?.data?.response?.apiResponseDecoded)) {
          return payload.data.response.apiResponseDecoded;
        }

        if (Array.isArray(payload?.data?.apiResponseDecoded)) {
          return payload.data.apiResponseDecoded;
        }

        // Safe fallbacks for older service return shapes.
        if (Array.isArray(payload?.response)) return payload.response;
        if (Array.isArray(payload?.data)) return payload.data;

        return [];
      };

      const categories = extractCategories(data);
      console.log("Instamart catalogue raw response:", data);
      console.log("Instamart apiResponseDecoded categories:", categories);
      setCatalogueStore(categories);
    } catch (error) {
      console.error("Catalogue API error:", error);

      setCatalogueStore([]);
    } finally {
      setLoading(false);

      if (preserveScroll && currentScrollY !== null) {
        window.scrollTo({ top: currentScrollY, behavior: "auto" });
      }
    }
  };

  useEffect(() => {
    if (userID) allcatalogueAPI();
  }, [userID]);

  // ── FILTER LOGIC (from File 2) ────────────────────────────────────────────

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    const categories = Array.isArray(catalogueStore) ? catalogueStore : [];

    if (!q) return categories;

    return categories

      .map((category) => {
        const filteredItems = (category.menu_items || []).filter((item) => {
          const firstPrice = item?.prices?.[0];

          return (
            String(category.category_name || "")
              .toLowerCase()

              .includes(q) ||
            String(item.item_name || "")
              .toLowerCase()

              .includes(q) ||
            String(item.item_id || "")
              .toLowerCase()

              .includes(q) ||
            String(firstPrice?.barcodeNumber || "")
              .toLowerCase()

              .includes(q)
          );
        });

        return {
          ...category,

          menu_items: filteredItems,
        };
      })

      .filter((category) => category.menu_items.length > 0);
  }, [catalogueStore, search]);

  // ── SELECT LOGIC (from File 2) ────────────────────────────────────────────

  // Handles is_sync: "Y" | 1 | true

  const canSelectItem = (item) => {
    return !(
      item?.is_sync === "Y" ||
      item?.is_sync === 1 ||
      item?.is_sync === true
    );
  };

  const toggleItem = (item) => {
    if (!canSelectItem(item)) return;

    setSelectedItems((prev) => ({
      ...prev,

      [item.item_id]: !prev[item.item_id],
    }));
  };

  // Only toggles selectable items; checkbox reflects real checked state

  const toggleCategoryItems = (category) => {
    const selectableItems = (category.menu_items || []).filter((item) =>
      canSelectItem(item),
    );

    const allSelected =
      selectableItems.length > 0 &&
      selectableItems.every((item) => selectedItems[item.item_id]);

    const updated = { ...selectedItems };

    selectableItems.forEach((item) => {
      updated[item.item_id] = !allSelected;
    });

    setSelectedItems(updated);
  };

  // Flat list of all visible items (across filtered categories)

  const allVisibleItems = useMemo(() => {
    return (
      Array.isArray(filteredCategories) ? filteredCategories : []
    ).flatMap((category) =>
      Array.isArray(category?.menu_items) ? category.menu_items : [],
    );
  }, [filteredCategories]);

  // Only selectable + checked items go into the sync payload

  const selectedSyncItems = useMemo(() => {
    return allVisibleItems.filter(
      (item) => canSelectItem(item) && selectedItems[item.item_id],
    );
  }, [allVisibleItems, selectedItems]);

  const hasSelectedItems = selectedSyncItems.length > 0;

  const syncPayload = useMemo(
    () => ({
      fk_userid: String(userID),

      itemlist: selectedSyncItems.map((i) => ({
        itemid: String(i.item_id),
      })),
    }),

    [selectedSyncItems, userID],
  );

  const handleConfirmSync = async () => {
    try {
      setSyncLoading(true);

      await syncInstamartItems(syncPayload);

      setSelectedItems({});

      setShowConfirm(false);

      await allcatalogueAPI(true);
    } catch (error) {
      console.error("Sync error:", error);
    } finally {
      setSyncLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);

    await allcatalogueAPI(true);

    setRefreshing(false);
  };

  return (
    <>
      <NoIndex />

      <div className="p-6 h-full flex flex-col overflow-hidden">
        {/* Header */}

        <div className="sticky top-0 z-30">
          <div className="flex  justify-between items-center mb-5 montserrat-font-1">
            <h1 className="text-xl font-semibold montserrat-font-1">
              Sell on Instamart
            </h1>

            <div className="flex gap-3 montserrat-font-1">
              <input
                className="border px-3 py-2 rounded-md w-72 text-sm"
                placeholder="Search category / item / barcode..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />

              <button
                onClick={handleRefresh}
                className="border px-3 py-2 rounded-md text-sm bg-blue-500 text-white flex items-center gap-2"
                title="Refresh"
                type="button"
              >
                <RefreshCcw
                  size={16}
                  className={refreshing ? "animate-spin" : ""}
                />
                Refresh
              </button>

              <button
                disabled={!hasSelectedItems}
                onClick={() => setShowConfirm(true)}
                className="bg-green-600 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50"
                type="button"
              >
                Sync to Instamart
              </button>
            </div>
          </div>
        </div>

        {/* Body */}

        <div className="flex-1 overflow-y-auto min-h-0">
          {loading ? (
            <div className="flex items-center justify-center h-[60vh] montserrat-font-1">
              <LoadingScreen text="Loading catalogue..." />
            </div>
          ) : filteredCategories.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              No catalogue data found.
            </div>
          ) : (
            filteredCategories.map((cat, index) => {
              const items = cat.menu_items || [];

              const selectableItems = items.filter((item) =>
                canSelectItem(item),
              );

              const selectedCount = selectableItems.filter(
                (item) => selectedItems[item.item_id],
              ).length;

              return (
                <div
                  key={`${cat.cat_id || cat.category_name}-${index}`}
                  className="border rounded-xl mb-6 bg-white montserrat-font-1"
                >
                  {/* Category Header */}

                  <div className="flex justify-between items-center p-4 border-b bg-white montserrat-font-1">
                    <div>
                      <h2 className="font-semibold text-sm montserrat-font-1">
                        {cat.category_name || "Unnamed Category"}
                      </h2>

                      <p className="text-xs text-gray-500">
                        {items.length} items • {selectedCount} selected
                      </p>
                    </div>

                    <label
                      className="flex items-center gap-2 text-sm"
                      style={{
                        cursor: selectableItems.length
                          ? "pointer"
                          : "not-allowed",

                        opacity: selectableItems.length ? 1 : 0.5,
                      }}
                    >
                      <input
                        type="checkbox"
                        disabled={!selectableItems.length}
                        checked={
                          selectableItems.length > 0 &&
                          selectableItems.every(
                            (item) => selectedItems[item.item_id],
                          )
                        }
                        onChange={() => toggleCategoryItems(cat)}
                      />
                      Select all
                    </label>
                  </div>

                  {/* Table Header */}

                  <div className="max-h-[500px] overflow-y-auto">
                    <div className=" sticky top-0 z-10 grid grid-cols-7 text-sm text-gray-800 bg-green-100 font-semibold px-4 py-2 border-b montserrat-font-1">
                      <div>Select</div>

                      <div>Image</div>

                      <div>Name</div>

                      <div>Item ID</div>

                      <div>Price</div>

                      <div>Barcode</div>

                      <div>Status</div>
                    </div>

                    {/* Rows */}

                    {items.map((item) => {
                      const firstPrice = item?.prices?.[0] || {};

                      const selectable = canSelectItem(item);

                      return (
                        <div
                          key={item.item_id}
                          className="grid grid-cols-7 overflow-auto items-center px-4 py-3 border-b text-sm montserrat-font-1"
                        >
                          <input
                            type="checkbox"
                            disabled={!selectable}
                            checked={!!selectedItems[item.item_id]}
                            onChange={() => toggleItem(item)}
                          />

                          <div className="w-10 h-10 rounded-full border flex items-center justify-center overflow-hidden bg-gray-100 montserrat-font-1">
                            {(item?.productImage && item.productImage !== "") ||
                              (item?.photo && item.photo !== "") ? (
                              <img
                                src={item.productImage || item.photo}
                                alt={
                                  item?.productName ||
                                  item?.item_name ||
                                  "product"
                                }
                                onError={(e) => {
                                  e.target.onerror = null;

                                  e.target.style.display = "none";
                                }}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <Image size={20} className="text-gray-400" />
                            )}
                          </div>

                          <div className="montserrat-font-1">
                            <div className="font-medium ">
                              {item.item_name || "—"}
                            </div>

                            <div className="text-xs text-gray-400">
                              {item.item_description || "No description"}
                            </div>
                          </div>

                          <div>{item.item_id || "—"}</div>

                          <div>₹{firstPrice?.price ?? "0"}</div>

                          <div>{firstPrice?.barcodeNumber || "—"}</div>

                          <div>
                            <span
                              style={{
                                background: selectable ? "#fef3c7" : "#dcfce7",

                                color: selectable ? "#92400e" : "#166534",

                                borderRadius: "999px",

                                padding: "4px 10px",

                                fontSize: "12px",

                                fontWeight: 600,
                              }}
                            >
                              {selectable ? "Pending Sync" : "Synced"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Confirm Modal */}

        {showConfirm && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 montserrat-font-1">
            <div className="bg-white w-[400px] p-5 rounded-xl montserrat-font-1">
              <h2 className="font-semibold mb-2">Confirm Sync</h2>

              <p className="text-sm text-gray-500 mb-3">
                Are you sure you want to sync these items to Instamart?
              </p>

              <div className="max-h-52 overflow-y-auto mb-4 text-sm border rounded-lg p-3 bg-gray-50">
                {selectedSyncItems.map((item) => (
                  <div
                    key={item.item_id}
                    className="py-2 border-b last:border-0"
                  >
                    <div className="font-semibold">{item.item_name}</div>

                    <div className="text-xs text-gray-500">
                      Item ID: {item.item_id}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-2 montserrat-font-1">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={syncLoading}
                  className="border px-3 py-1 rounded text-sm"
                >
                  Cancel
                </button>

                <button
                  onClick={handleConfirmSync}
                  disabled={syncLoading || !hasSelectedItems}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm disabled:opacity-50"
                >
                  {syncLoading ? "Syncing..." : "Confirm Sync"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default SellOnInstamart;